/**
 * Zustand Store for Processing State Management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  ProcessingState,
  ProcessingStep,
  ProcessingResult,
  RoleConfigurations,
  LaTeXOptions,
} from '@shared/types';

/**
 * Normalize URL by removing trailing slash
 */
function normalizeUrl(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

interface ProcessingStore {
  // State
  processId: string | null;
  state: ProcessingState | null;
  result: ProcessingResult | null;
  roleConfigurations: RoleConfigurations | null;
  latexOptions: LaTeXOptions | null;
  serverUrl: string;

  // Actions
  setProcessId: (processId: string) => void;
  setState: (state: ProcessingState) => void;
  setResult: (result: ProcessingResult) => void;
  setRoleConfigurations: (configs: RoleConfigurations) => void;
  setLatexOptions: (options: LaTeXOptions) => void;
  setServerUrl: (url: string) => void;
  reset: () => void;

  // Computed
  isProcessing: () => boolean;
  getCurrentProgress: () => number;
  getCurrentMessage: () => string;
}

const initialState = {
  processId: null,
  state: null,
  result: null,
  roleConfigurations: null,
  latexOptions: null,
  // Hardcode the URL and normalize it (remove trailing slash)
  serverUrl: normalizeUrl('https://auto-problem-maker-worker.playphysics.net'),
};

export const useProcessingStore = create<ProcessingStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setProcessId: (processId) => set({ processId }),

        setState: (state) => set({ state }),

        setResult: (result) => set({ result }),

        setRoleConfigurations: (configs) => set({ roleConfigurations: configs }),

        setLatexOptions: (options) => set({ latexOptions: options }),

        setServerUrl: (url) => {
          // Always normalize URL to remove trailing slash
          set({ serverUrl: normalizeUrl(url) });
        },

        reset: () => set(initialState),

        isProcessing: () => {
          const state = get().state;
          if (!state) return false;
          return state.currentStep !== ProcessingStep.IDLE &&
                 state.currentStep !== ProcessingStep.COMPLETED &&
                 state.currentStep !== ProcessingStep.ERROR;
        },

        getCurrentProgress: () => {
          const state = get().state;
          return state?.progress || 0;
        },

        getCurrentMessage: () => {
          const state = get().state;
          return state?.currentAnimation || '准备就绪...';
        },
      }),
      {
        name: 'processing-store',
        partialize: (state) => ({
          roleConfigurations: state.roleConfigurations,
          latexOptions: state.latexOptions,
          // Don't persist serverUrl - always use the hardcoded value from code
        }),
      }
    )
  )
);
